/**
 * ENHANCED TERRAIN's sea: the shader that draws the two coastal maps' `s = 10`
 * plate as water instead of as a flat blue quad.
 *
 * `flight/ocean-waves.ts` holds the wave train and `flight/ocean-look.ts` the
 * palette; this is the three.js/WebGPU plumbing around them, in the split
 * `ramp-material.ts` and `screen-sprites.ts` use. What is here and nowhere else
 * is the five decisions a fragment shader has to make that the arithmetic
 * cannot.
 *
 * ## 1. It is one material on one mesh, and it selects per face
 *
 * London places one sea plate (`TERR1-00`, 93 triangles, every one of them
 * `anchorIndex` 4) and Dunkirk places one (`TERR2-51`, 40 water faces **and 9 of
 * the far English shore painted ground-green**). So the gate cannot be the
 * object: it is `shadeAnchor === WATER_PALETTE_INDEX`, per fragment, which is
 * `world/water-mask.ts`'s own predicate — *a triangle whose `anchorIndex` is 4
 * is water* — asked of the vertex attribute `baked-model.ts` already writes for
 * `ramp-material.ts`. No new attribute, and the nine land faces come out of this
 * shader as `vertexColor()` exactly, which is the whole of what "the toggle is
 * off" means for them.
 *
 * The three inland regions place no `s >= 9` plate at all
 * (`shore-field.test.ts`'s census), so they never build this material. Their
 * rivers and ponds are `anchorIndex` 4 too and are deliberately **left flat** —
 * a 40 m river with a 193 m swell running through it would be worse than the
 * plate it replaced, and this feature was asked for at sea.
 *
 * ## 2. Detail is filtered by its own screen size, not by a distance table
 *
 * `fwidth` of the fragment's world position is metres-per-pixel *on this
 * surface*, which already contains the range, the field of view, the viewport
 * and the grazing angle. An octave whose wavelength is under
 * `DETAIL_GONE_PX` pixels is faded out entirely and one over `DETAIL_FULL_PX`
 * is drawn whole. That is what keeps the sea from crawling at altitude, and it
 * needs no uniform, no resize hook and no per-machine tuning — which is the
 * "one shader for every machine" rule (`roadmap.md`, 2026-09-02) applied to the
 * one place a lane would normally reach for a device query.
 *
 * **What is filtered away goes into the lobe, not into nothing.** Removing a
 * normal removes the light it was catching, and a sea that loses its glitter at
 * patrol height is a sheet of painted metal. `ocean-look.ts`'s two half-angles
 * are interpolated by how much detail was filtered, so the specular lobe
 * *broadens* as the ripples go sub-pixel and the glitter road survives to the
 * altitude the feature was built for.
 *
 * ## 3. The phase is anchored, because 600 km and 10 m do not share a float32
 *
 * See `ocean-waves.ts`'s header. The shader is handed the anchor and one phase
 * per octave and evaluates only `fragment − anchor`, so nothing large is ever
 * inside a `sin`. `setFrame` is called once per rendered frame from `scene-draw.ts`
 * with the **encounter's** clock and the player's own pose.
 *
 * ## 4. Every colour is a palette byte, and the stipple is the port's own
 *
 * The base is a `WATER_RAMP` texel, fetched `NearestFilter` out of a 7 x 1
 * lookup exactly the way `ramp-material.ts` fetches `BARON.PAL`. All **three**
 * things that vary it — the reflected-sky term, the shoal and the sun's broad
 * path — move the *ramp position*, an integer, and are resolved between two
 * adjacent entries by `ordered-dither.ts`'s own 4x4 Bayer threshold, which is
 * what a 1990 artist did with a remainder and what stops a smooth swell showing
 * contour bands. Sharing that threshold rather than inventing a second one is
 * load-bearing: two stipples on one screen have to dissolve on the same ladder
 * or they moiré (that module's header).
 *
 * The **specular core** is the one term that leaves the palette, and it is
 * handled the way `enhanced-sky.ts` handles its glare — radiance, then that
 * file's own tone curve, then the DAC grid. It is added as a *delta* over the
 * base rather than folded into it, so where there is no core the delta is
 * exactly zero and the pixel is exactly the palette byte
 * (`ocean-palette.test.ts` pins that identity). The broad half of the reflection
 * was radiance too in the first build and put **93.6 % of a real frame's pixels
 * off-palette**; `ocean-look.ts`'s `SUN_PATH_STEPS` is the rule that came out of
 * that measurement.
 *
 * ## 5. Why the waves are visible at all when the sun is overhead
 *
 * The first build shaded wave faces by `dot(normal, sun)`, and at noon the sea
 * came out flat: a gentle sea's normals are within 12.6° of straight up and an
 * overhead sun barely distinguishes them. The mechanism that actually makes a
 * sea look like a sea is **reflected elevation** — a facet tilted toward the
 * horizon shows the bright hazy skyline and one tilted up shows the dark zenith
 * — and it maps onto `WATER_RAMP` without inventing anything, because that run
 * goes from `#203449` to `#416582` in exactly that direction. It also gets the
 * viewing geometry right for free: a normal tilted by θ swings the reflected ray
 * by 2θ, so the swell is legible from a cockpit at altitude and flattens out
 * when you look straight down at it, which is what it does.
 */

import * as THREE from "three/webgpu";
import { SHADE_ANCHOR_ATTRIBUTE } from "./ramp-material.ts";
import { ditherThreshold, quantiseToVgaDac, VGA_DAC_LEVELS, type FloatNode } from "./ordered-dither.ts";
import { toneMap, toneMapChannel } from "../flight/enhanced-sky.ts";
import { WATER_PALETTE_INDEX } from "../map/map-features.ts";
import { OCEAN_WAVES, oceanWavePhases } from "../flight/ocean-waves.ts";
import {
  glitterLobeExponents,
  BROAD_GAIN_SHARE,
  FRESNEL_FLOOR,
  SHORE_RAMP_LIFT,
  WATER_F0,
  WAVE_SHADE_STEPS,
  type OceanLook,
} from "../flight/ocean-look.ts";
import type { ShoreField } from "../world/shore-field.ts";

/**
 * An octave narrower than this many pixels contributes nothing; over
 * `DETAIL_FULL_PX` it contributes everything.
 *
 * A wave four pixels across is already at Nyquist, and a ramp walk that
 * quantises an aliased slope turns the alias into a stipple that crawls. The
 * band is therefore two full pixels of half-wave at the bottom and a whole
 * eight-pixel wave at the top, which is conservative on purpose — the contrast
 * that filtering costs is given back by `REFLECT_SHADE_GAIN`'s renormalisation
 * rather than by drawing waves the framebuffer cannot resolve.
 */
export const DETAIL_GONE_PX = 2.5;
export const DETAIL_FULL_PX = 8;

/**
 * How far the shoal band's edge wanders with the sea running through it,
 * metres.
 *
 * The field is 156 m a cell (`shore-field.ts`) and a bilinear fetch off it draws
 * a smooth but unmistakably rectilinear contour. Offsetting the *lookup* by the
 * surface slope breaks that contour on the waves' own quasi-periodic field, so
 * the band's edge wanders the way a real one does and costs one `vec2` add — the
 * slope was computed two lines earlier for the shading.
 */
export const SHORE_EDGE_WANDER_M = 260;

/**
 * How hard the reflected-elevation term drives the ramp walk.
 *
 * Sized against the **RMS** slope rather than the bound. `ocean-waves.ts` caps
 * the surface slope at 0.224 but eight cosines at random relative phase have an
 * RMS slope of `√8 · A·k / √2` = 0.056, and a normal tilted by θ swings the
 * reflected ray by 2θ — so the elevation term's typical magnitude at a grazing
 * view is about 0.10, not 0.45. At 2.4 (the first shipped value) that put the
 * average facet a *half* step off the base: measured on a real frame at
 * 7 830 ft, **89 % of sea pixels were the base entry** and the sea read as a
 * plate with a stipple on it. 5.5 puts the average facet a whole step off and
 * the steep ones at the ±`WAVE_SHADE_STEPS` end stops.
 */
export const REFLECT_SHADE_GAIN = 5.5;

/**
 * The floor under the detail renormalisation — and the other half of "what the
 * filter takes off the normal has to go somewhere".
 *
 * Filtering removes slope *variance*, so the surviving RMS slope falls like
 * `√detail`; dividing the shade term by that keeps the sea's contrast roughly
 * constant as the fine octaves go sub-pixel, instead of letting it fade to a
 * flat plate at exactly the range a pilot spends the sortie looking at. It is
 * the same trade `ocean-look.ts`'s two lobe half-angles make for the glitter,
 * and it is floored because at extreme range only the swell is left and
 * amplifying one octave to full contrast would be drawing a wave the frame
 * cannot resolve.
 */
export const DETAIL_GAIN_FLOOR = 0.25;

/** How close to the window's rim the shoal starts being ignored — see `shore-field.ts` on why outside it is deep. */
const WINDOW_FADE = 0.03;

/** A floor under the pixel footprint, so a fragment whose derivatives came out zero (a degenerate quad at the horizon) cannot divide by it. */
const MIN_FOOTPRINT_M = 1e-4;

type Vec3Node = THREE.Node<"vec3">;

/**
 * One scalar uniform. Spelled through a factory so the type is *inferred* from
 * three's own generic rather than restated — `ordered-dither.ts`'s `FloatNode`
 * exists for the same reason and gives the same warning about guessing at TSL's
 * overloads.
 */
function scalarUniform() {
  return THREE.TSL.uniform(0);
}
type ScalarUniform = ReturnType<typeof scalarUniform>;

/**
 * Everything the frame loop can move: the anchor, and one phase per octave.
 * Seven floats in all — deliberately small, because the 2026-09-02 Safari lesson
 * is that a uniform arrangement is a code path and this one has to compile
 * identically on every backend.
 */
interface OceanUniforms {
  readonly anchorX: ScalarUniform;
  readonly anchorZ: ScalarUniform;
  readonly phases: readonly ScalarUniform[];
}

export interface OceanMaterial {
  readonly material: THREE.MeshBasicNodeMaterial;
  /**
   * Advances the sea. `timeS` is `EncounterState.timeS` — the sim's clock, never
   * the wall clock — and `anchorX`/`anchorZ` are the player's own pose, which is
   * the nearest deterministic point to every camera in the scene.
   */
  setFrame(timeS: number, anchorX: number, anchorZ: number): void;
  dispose(): void;
}

/**
 * One channel of the final composition, in plain arithmetic — the scalar twin of
 * the last line of `oceanColorNode`, so `ocean-palette.test.ts` can pin the
 * property the whole palette claim rests on without a GPU: **with no specular
 * core the output is the input, exactly.**
 *
 * `dither` is `ordered-dither.ts`'s threshold, which is strictly below 1, so a
 * zero delta floors to zero and the pixel is the `WATER_RAMP` byte the texel
 * held. It is written as a *delta* rather than as `quantise(toneMap(base + lit))`
 * for exactly that reason: the DAC grid `k/63` and `BARON.PAL`'s own
 * `(v << 2) | (v >> 4)` bytes are not the same lattice, so quantising a palette
 * entry moves it about one step — which is fine for a sky gradient that was
 * never on the palette and wrong for a plate of water that was.
 */
export function oceanPixelChannel(base: number, core: number, dither: number): number {
  const levels = VGA_DAC_LEVELS - 1;
  const delta = Math.max(toneMapChannel(base + core) - base, 0);
  return base + Math.floor(delta * levels + dither) / levels;
}

function paletteVec3(colour: number): Vec3Node {
  return THREE.TSL.vec3(((colour >> 16) & 0xff) / 255, ((colour >> 8) & 0xff) / 255, (colour & 0xff) / 255);
}

/** `WATER_RAMP` as a 7 x 1 `NearestFilter` lookup — `ramp-material.ts`'s palette texture, one run wide. */
function rampTexture(ramp: readonly number[]): THREE.DataTexture {
  const texels = new Uint8Array(ramp.length * 4);
  ramp.forEach((colour, at) => {
    texels[at * 4] = (colour >> 16) & 0xff;
    texels[at * 4 + 1] = (colour >> 8) & 0xff;
    texels[at * 4 + 2] = colour & 0xff;
    texels[at * 4 + 3] = 255;
  });
  const texture = new THREE.DataTexture(texels, ramp.length, 1, THREE.RGBAFormat);
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  // `NoColorSpace`, for `ramp-material.ts`'s reason: this app feeds `BARON.PAL`
  // bytes straight through and `webgpu-support.ts` has colour management off.
  texture.colorSpace = THREE.NoColorSpace;
  texture.needsUpdate = true;
  return texture;
}

/** The shoal field as a single-channel texture. Linear, because the band is a gradient and a `NearestFilter` fetch would step it in 156 m squares. */
function shoreTexture(field: ShoreField): THREE.DataTexture {
  const texture = new THREE.DataTexture(field.data, field.cells, field.cells, THREE.RedFormat);
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.generateMipmaps = false;
  texture.colorSpace = THREE.NoColorSpace;
  texture.needsUpdate = true;
  return texture;
}

/** The surface gradient and how much of the train survived filtering, from one walk of the octaves. */
function waveField(local: THREE.Node<"vec2">, uniforms: OceanUniforms): {
  readonly slope: THREE.Node<"vec2">;
  readonly detail: FloatNode;
} {
  // Metres a pixel covers *on this surface*: both derivatives, because a sea
  // seen edge-on stretches along one screen axis and not the other.
  const footprint = THREE.TSL.max(
    THREE.TSL.max(THREE.TSL.length(THREE.TSL.dFdx(local)), THREE.TSL.length(THREE.TSL.dFdy(local))),
    MIN_FOOTPRINT_M,
  );
  let slope: THREE.Node<"vec2"> = THREE.TSL.vec2(0, 0);
  let detail: FloatNode = THREE.TSL.dot(THREE.TSL.vec2(0, 0), THREE.TSL.vec2(0, 0));
  OCEAN_WAVES.forEach((wave, octave) => {
    const direction = THREE.TSL.vec2(wave.directionX, wave.directionZ);
    const phase = uniforms.phases[octave];
    const cycles = THREE.TSL.dot(local, direction).div(wave.wavelengthM);
    const phased = phase ? cycles.add(phase) : cycles;
    const pixels = THREE.TSL.float(wave.wavelengthM).div(footprint);
    const fade = THREE.TSL.smoothstep(DETAIL_GONE_PX, DETAIL_FULL_PX, pixels);
    const wavenumber = (2 * Math.PI) / wave.wavelengthM;
    const term = THREE.TSL.cos(phased.mul(2 * Math.PI))
      .mul(wave.amplitudeM * wavenumber)
      .mul(fade);
    slope = slope.add(direction.mul(term));
    detail = detail.add(fade);
  });
  return { slope, detail: detail.div(OCEAN_WAVES.length) };
}

/** How shallow this fragment is, `0` in deep water and `1` at the waterline, with the band's edge run through the waves. */
function shoalAtFragment(shore: THREE.DataTexture, field: ShoreField, slope: THREE.Node<"vec2">): FloatNode {
  const flat = THREE.TSL.vec2(THREE.TSL.positionWorld.x, THREE.TSL.positionWorld.z);
  const wandered = flat.add(slope.mul(SHORE_EDGE_WANDER_M));
  const uv = wandered.sub(THREE.TSL.vec2(field.minX, field.minZ)).div(field.spanM);
  const distance = THREE.TSL.texture(shore, uv.clamp(0, 1)).r;
  // Past the window's rim there is no field, and the honest answer for open sea
  // is "deep" rather than whatever the edge texel happens to hold.
  const rim = THREE.TSL.max(uv.x.sub(0.5).abs(), uv.y.sub(0.5).abs());
  const inside = THREE.TSL.smoothstep(0.5, 0.5 - WINDOW_FADE, rim);
  return distance.oneMinus().mul(inside);
}

/**
 * Everything the sun (or the moon) throws back, split the way `ocean-look.ts`'s
 * `SUN_PATH_STEPS` argues for: the broad reflection is a **ramp walk** and only
 * the tight core is radiance.
 */
function sunlight(look: OceanLook, normal: Vec3Node, view: Vec3Node, detail: FloatNode): {
  readonly path: FloatNode;
  readonly core: Vec3Node;
} {
  const lobes = glitterLobeExponents();
  const sun = THREE.TSL.vec3(look.sun.x, look.sun.y, look.sun.z);
  const half = THREE.TSL.normalize(view.add(sun));
  const closeness = THREE.TSL.dot(normal, half).clamp(0, 1);
  const schlick = THREE.TSL.pow(THREE.TSL.dot(normal, view).clamp(0, 1).oneMinus(), 5)
    .mul(1 - WATER_F0)
    .add(WATER_F0);
  const fresnel = schlick.mul(1 - FRESNEL_FLOOR).add(FRESNEL_FLOOR);
  // The filtered-away normals become roughness rather than nothing — see the
  // header's §2, and `ocean-look.ts` for why the sea would otherwise go dead at
  // exactly the altitude this feature exists for. Broadening a lobe at constant
  // peak *adds* energy, so the gain comes down with it (`BROAD_GAIN_SHARE`).
  const exponent = THREE.TSL.mix(lobes.broad, lobes.sharp, detail);
  const gain = THREE.TSL.mix(BROAD_GAIN_SHARE, 1, detail).mul(look.glitterCoreGain);
  return {
    path: THREE.TSL.pow(closeness, lobes.broad).mul(fresnel),
    core: paletteVec3(look.glitterCore).mul(THREE.TSL.pow(closeness, exponent).mul(gain).mul(fresnel)),
  };
}

/** The sea's own colour node: ramp walk, stipple, glitter, and the gate that hands every other face straight back. */
function oceanColorNode(
  look: OceanLook,
  field: ShoreField,
  shore: THREE.DataTexture,
  ramp: THREE.DataTexture,
  uniforms: OceanUniforms,
): Vec3Node {
  const anchor = THREE.TSL.vec2(uniforms.anchorX, uniforms.anchorZ);
  const local = THREE.TSL.vec2(THREE.TSL.positionWorld.x, THREE.TSL.positionWorld.z).sub(anchor);
  const { slope, detail } = waveField(local, uniforms);
  const normal = THREE.TSL.normalize(THREE.TSL.vec3(slope.x.negate(), 1, slope.y.negate()));
  const view = THREE.TSL.normalize(THREE.TSL.cameraPosition.sub(THREE.TSL.positionWorld));

  // The reflected ray, and how far its elevation has moved off the one flat
  // water would have given — see the header's §5. Up is dark, down is hazy, and
  // `WATER_RAMP` runs in exactly that direction.
  const reflected = normal.mul(THREE.TSL.dot(normal, view).mul(2)).sub(view);
  const survivingRms = THREE.TSL.sqrt(THREE.TSL.max(detail, DETAIL_GAIN_FLOOR));
  const shade = reflected.y.sub(view.y).mul(-REFLECT_SHADE_GAIN).div(survivingRms).clamp(-1, 1);

  const shoal = shoalAtFragment(shore, field, slope);
  const sun = sunlight(look, normal, view, detail);
  // One walk along the run: the base for this hour, the wave face, the shoal,
  // the sun's broad path — then the port's own Bayer threshold, so a position
  // between two entries stipples between them instead of banding.
  const walked = shade
    .mul(WAVE_SHADE_STEPS)
    .add(shoal.mul(SHORE_RAMP_LIFT))
    .add(sun.path.mul(look.sunPathSteps))
    .add(look.baseIndex)
    .add(ditherThreshold());
  const index = THREE.TSL.floor(walked).clamp(0, look.ramp.length - 1);
  const base = THREE.TSL.texture(ramp, THREE.TSL.vec2(index.add(0.5).div(look.ramp.length), 0.5)).rgb;

  // The specular core goes on as a **delta**: with none of it the difference is
  // exactly zero, the quantiser floors it to zero, and the pixel is the palette
  // byte the texel held. See the header's §4.
  const lit = toneMap(base.add(sun.core));
  return base.add(quantiseToVgaDac(lit.sub(base).max(0)));
}

/**
 * The sea, as a drop-in for the instancer's shared
 * `MeshBasicMaterial({ vertexColors: true, side: DoubleSide })` — same flags,
 * same double-sidedness, no transparency anywhere, so it stays clear of the
 * reversed-Z trap that draws a `transparent` mesh as zero pixels and the ship
 * wakes two centimetres above it compose exactly as they did.
 */
export function createOceanMaterial(look: OceanLook, field: ShoreField): OceanMaterial {
  const uniforms: OceanUniforms = {
    anchorX: scalarUniform(),
    anchorZ: scalarUniform(),
    phases: OCEAN_WAVES.map(() => scalarUniform()),
  };
  const shore = shoreTexture(field);
  const ramp = rampTexture([...look.ramp]);
  const material = new THREE.MeshBasicNodeMaterial({ side: THREE.DoubleSide });
  const anchorAttribute = THREE.TSL.attribute<"float">(SHADE_ANCHOR_ATTRIBUTE, "float");
  const isWater = anchorAttribute.sub(WATER_PALETTE_INDEX).abs().lessThan(0.5);
  material.colorNode = THREE.TSL.select(
    isWater,
    oceanColorNode(look, field, shore, ramp, uniforms),
    THREE.TSL.vertexColor().rgb,
  );
  return {
    material,
    setFrame: (timeS, anchorX, anchorZ) => {
      const phases = oceanWavePhases(anchorX, anchorZ, timeS);
      uniforms.anchorX.value = Math.fround(anchorX);
      uniforms.anchorZ.value = Math.fround(anchorZ);
      phases.forEach((phase, octave) => {
        const slot = uniforms.phases[octave];
        if (slot) slot.value = phase;
      });
    },
    dispose: () => {
      shore.dispose();
      ramp.dispose();
      material.dispose();
    },
  };
}
